import { Text } from '@digibee/beehive-ui';
import { iff } from '@digibee/control-statements';
import { BaseComponent } from '@digibee/flow';
import { useSelector } from '@xstate/react';
import { useMemo } from 'react';

import { TreeNode } from '../Node';
import * as Elements from '../Tree.elements';
import useTreeContext from '../utils/useTreeContext';

import MockTag from '~/components/MockTag';
import { MockType } from '~/entities/Mock';
import { usePipelineStore } from '~/scenes/Build/scenes/FlowBuilder/stores/pipelineStore';

type ComponentNodeProps = {
  node: TreeNode<BaseComponent>;
  shouldInspectDebugLayout: boolean;
};

const ComponentNode = ({
  node,
  shouldInspectDebugLayout
}: ComponentNodeProps) => {
  const mocks = usePipelineStore(state => state.pipeline?.mocks);
  const hasMockActive = mocks?.find(
    (mock: MockType) => mock.connectorId === node.id && mock.enabled
  );

  const actor = useTreeContext(state => state.actor);
  const actionsSlot = useTreeContext(state => state.actionsSlot);
  const mapConnectorImage = useTreeContext(state => state.mapConnectorImage);
  const onItemDoubleClick = useTreeContext(state => state.onItemDoubleClick);

  const isCurrentLevelComponent = useSelector(
    actor,
    state => state.context.currentLevelComponent?.id() === node.id
  );

  const comesFromCurrentLevelComponent = useSelector(actor, state =>
    state.context.currentLevelComponent
      ? node.item.comesFromComponent(state.context.currentLevelComponent.id())
      : false
  );

  const emphasis =
    // eslint-disable-next-line no-nested-ternary
    isCurrentLevelComponent && !shouldInspectDebugLayout
      ? 'component'
      : comesFromCurrentLevelComponent && !shouldInspectDebugLayout
        ? 'children'
        : undefined;

  const actionsSlotRendered = actionsSlot?.({
    item: node.item
  });

  const { shape, image } = useMemo(
    () => mapConnectorImage(node.item.id()),
    [node.item]
  );

  return (
    <Elements.Container
      data-testid='flow-tree-node-container'
      shouldInspectDebugLayout={shouldInspectDebugLayout}
    >
      <Elements.Item
        onDoubleClick={() => {
          onItemDoubleClick(node.item);
        }}
        depthLeft={node.depth}
        emphasis={emphasis}
        data-testid='build-flow-tree-node-item'
        shouldInspectDebugLayout={shouldInspectDebugLayout}
      >
        {shouldInspectDebugLayout && (
          <Elements.MockTagContainer
            shouldInspectDebugLayout={shouldInspectDebugLayout}
            hasMockActive={!!hasMockActive}
          >
            <MockTag isFlowTree />
          </Elements.MockTagContainer>
        )}
        <Elements.Shape2 shape={shape} image={image} />
        {hasMockActive && !shouldInspectDebugLayout && (
          <Elements.MockTagContainer
            shouldInspectDebugLayout={shouldInspectDebugLayout}
          >
            <MockTag isFlowTree />
          </Elements.MockTagContainer>
        )}
        <Elements.Label shouldInspectDebugLayout={shouldInspectDebugLayout}>
          <Text
            variant='b3'
            css={{
              fontWeight: 400,
              color: '$neu500',
              fontSize: shouldInspectDebugLayout ? '12px' : '14px',
              _truncate: '$12'
            }}
            title={node.label}
          >
            {node.label}
          </Text>
        </Elements.Label>
        {iff(!!actionsSlotRendered, () => (
          <Elements.FloatAction
            shouldInspectDebugLayout={shouldInspectDebugLayout}
          >
            {actionsSlotRendered}
          </Elements.FloatAction>
        ))}
      </Elements.Item>
    </Elements.Container>
  );
};

export default ComponentNode;
